import { saveAs } from "file-saver";
import React from "react";
import { WithTranslation, withTranslation } from "react-i18next";

import { TValue } from "../../../global.types";
import TestIds from "../../../testIds";
import BusyIndicator from "../../busyIndicator/BusyIndicator";
import { Toolbar } from "../../toolbar/Toolbar";
import { StyledFileViewer } from "../FileViewers.styles";
import {
    FileToolbarItem,
    getFileNameFromSrc,
    getSharedToolbarDefinition,
    IFileViewerProps
} from "../FileViewers.utils";
import NoPreviewSquirrel from "../NoPreviewSquirrel";
import { Header } from "../pdfViewer/PDFViewer.style";
import { FileText } from "./TextViewer.style";

interface IProps extends IFileViewerProps, WithTranslation {
    formatter?: (text: string) => string;
}

interface IState {
    loaded: boolean;
    text: string;
    error: string;
}

class TextViewer extends React.PureComponent<IProps, IState> {
    static defaultProps: Partial<IProps> = {
        hiddenItems: []
    };

    state = {
        loaded: false,
        text: "",
        error: ""
    };

    componentDidMount() {
        this.init();
    }

    componentDidUpdate(prevProps: IProps, prevState: IState) {
        if (this.props.src !== prevProps.src) {
            this.init();
        }
    }

    init = async () => {
        if (!this.props.src) {
            return;
        }

        this.setState({
            loaded: false
        });

        let text = "";
        let error = "";

        try {
            const res = await fetch(this.props.src);

            if (!res.ok) {
                error = this.props.t("Components:FileViewers.FetchFailed");
            } else {
                text = await res.text();
            }
        } catch (e) {
            error = e.message;
        }

        if (this.props.formatter) {
            text = this.props.formatter(text);
        }

        this.setState({
            text,
            error,
            loaded: true
        });
    };

    renderError = () => {
        return (
            <NoPreviewSquirrel text={this.state.error}/>
        );
    };

    handleItemChange = (id: string, value?: TValue) => {
        switch (id) {
            case FileToolbarItem.Dashboard:
                this.props.onGoToList?.();
                break;
            case FileToolbarItem.Download:
                saveAs(this.props.src, this.props.fileName ?? getFileNameFromSrc(this.props.src));
                break;
            case FileToolbarItem.Remove:
                this.props.onFileRemove?.(this.props.src);
                break;
        }
    };

    render() {
        const def =
            getSharedToolbarDefinition({
                ...this.props,
                context: this.props.context,
                onChatCreate: this.props.onChatCreate,
            });

        return (
            <StyledFileViewer data-testid={TestIds.FileViewer}>
                <Header>
                    <Toolbar
                        onItemChange={this.handleItemChange}
                        staticItems={def.staticItems}>
                        {def.items}
                    </Toolbar>
                </Header>
                {!this.state.loaded &&
                    <BusyIndicator/>
                }
                {!this.state.error &&
                    <FileText data-testid={TestIds.FileText}>
                        {this.state.text}
                    </FileText>
                }
                {this.state.error && this.renderError()}
            </StyledFileViewer>
        );
    }
}

export default withTranslation(["Components"])(TextViewer);